import { bound } from "@frui.ts/helpers/dist";
import { Router, watchBusy } from "@frui.ts/screens/dist";
import { Modules } from "config";
import copy from "copy-to-clipboard";
import { addDays } from "date-fns";
import type User from "entities/user";
import type { interfaces } from "inversify";
import { computed, when } from "mobx";
import LocalizationService from "services/localizationService";
import AppRouter from "services/router";
import { isExternalUser } from "../../../entities/externalUser";
import UserRepository from "../../../repositories/userRepository";
import EventBus, { invitationExtended } from "../../../services/eventBus";
import NotificationService, { NotificationType } from "../../../services/notificationService";
import { endOfCzechDay } from "utils/dateUtils";
import SecurityService from "../../../services/securityService";
import DetailViewModelWithChildren from "../../detailViewModelWithChildren";
import UserEditationViewModel from "./userEditationViewModel";
import UserIdentityProvidersViewModel from "./userIdentityProvidersViewModel";
import UserRolesViewModel, { rolesNavigationName } from "./userRolesViewModel";
import UsersViewModel from "./usersViewModel";

@Router.registerRoute({
  route: ":userId",
  name: Router.Self,
  children: [UserRolesViewModel, UserEditationViewModel, UserIdentityProvidersViewModel],
})
export default class UserDetailViewModel extends DetailViewModelWithChildren<
  User,
  UserRolesViewModel | UserEditationViewModel | UserIdentityProvidersViewModel
> {
  private userEditationViewModel: UserEditationViewModel;
  private userRolesViewModel: UserRolesViewModel;
  private userIdentityProvidersViewModel: UserIdentityProvidersViewModel;

  constructor(
    private itemId: string,
    private repository: UserRepository,
    private localizationService: LocalizationService,
    private appRouter: AppRouter,
    private eventBus: EventBus,
    private notificationService: NotificationService,
    private securityService: SecurityService,
    private userEditationViewModelFactory: ReturnType<typeof UserEditationViewModel.Factory>,
    private userRolesViewModelFactory: ReturnType<typeof UserRolesViewModel.Factory>,
    private userIdentityProvidersViewModelFactory: ReturnType<typeof UserIdentityProvidersViewModel.Factory>
  ) {
    super();
    this.navigationName = itemId;
  }

  @computed
  get name() {
    return this.translate("name").replace("%user%", this.item.username);
  }

  @computed
  get allowWrite() {
    return this.securityService.allowTo("users", "write");
  }

  @watchBusy
  protected async loadDetail() {
    const item = await this.repository.get(this.itemId);

    this.children.push((this.userEditationViewModel = this.userEditationViewModelFactory(item)));
    this.children.push((this.userRolesViewModel = this.userRolesViewModelFactory(item)));

    if (isExternalUser(item)) {
      this.children.push((this.userIdentityProvidersViewModel = this.userIdentityProvidersViewModelFactory(item)));
    }

    return item;
  }

  @computed
  get itemLoaded() {
    return this.item !== undefined;
  }

  @computed
  get invitationExpired() {
    return !this.item.isActive && this.item.invitation && this.item.invitation.expiresAt < new Date();
  }

  @bound
  @watchBusy
  async onActivationEmailClick() {
    try {
      await this.repository.activationEmail(this.item);
      this.notificationService.add({ type: NotificationType.SUCCESS, title: this.translate("activationEmailSent") });
    } catch (e) {
      // silent catch - global error handler do a dirty work
    }
  }

  @bound
  @watchBusy
  async onExtendInvitationClick() {
    try {
      const validity = addDays(endOfCzechDay(new Date()), 3);
      this.item.invitation = await this.repository.extendInvitation(this.item, validity);
      this.eventBus.publish(invitationExtended({ user: this.item, invitation: this.item.invitation }));
      this.notificationService.add({ type: NotificationType.SUCCESS, title: this.translate("invitationExtended") });
    } catch (e) {}
  }

  @bound
  onCopyActivationEmailClick() {
    if (this.item.invitation) {
      copy(this.item.invitation.activationUrl);
      this.notificationService.add({ type: NotificationType.INFO, title: this.translate("activationLinkCopiedToClipboard") });
    }
  }

  @bound
  close() {
    this.requestClose();
  }

  get backLink() {
    return this.appRouter.getUrl(UsersViewModel);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `users.userDetail.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateAttribute(attribute: keyof User, size?: number) {
    return this.localizationService.translateAttribute("user", attribute, size);
  }

  async findNavigationChild(navigationName: string | undefined) {
    await when(() => this.item !== undefined);
    if (navigationName === rolesNavigationName) {
      return this.tryActivateChild(this.userRolesViewModel);
    }

    return this.tryActivateChild(this.userEditationViewModel);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new UserDetailViewModel(
        itemId,
        container.get(UserRepository),
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(UserEditationViewModel.Factory),
        container.get(UserRolesViewModel.Factory),
        container.get(UserIdentityProvidersViewModel.Factory)
      );
    };
  }
}
